import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Ruler, Stack, FileText, Sparkle, GoogleLogo } from "@phosphor-icons/react";

const FEATURES = [
  { icon: <Ruler size={22} weight="duotone" />, title: "Drawing-Aware AI", desc: "Reads PDFs, elevations, plans, and details. Identifies every cabinet, panel, and molding." },
  { icon: <Stack size={22} weight="duotone" />, title: "Structured Takeoffs", desc: "Cabinets, hardware, linear footage, materials — fully itemized with dimensions." },
  { icon: <FileText size={22} weight="duotone" />, title: "Shop-Ready Quotes", desc: "Materials + labor + overhead + margin = clean PDFs you can send to clients in seconds." },
];

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-ink-950 text-ink-50">
      {/* Left panel */}
      <div className="relative flex flex-col justify-between p-10 lg:p-14 border-r border-ink-700">
        <div className="absolute inset-0 blueprint-grid opacity-40 pointer-events-none" />
        <div className="relative z-10">
          <div className="flex items-center gap-3" data-testid="brand-logo">
            <div className="h-9 w-9 rounded-md bg-amber flex items-center justify-center">
              <Ruler size={20} weight="bold" className="text-ink-950" />
            </div>
            <div>
              <div className="font-heading font-bold tracking-tight text-lg">JOINER</div>
              <div className="data-label">Cabinetry / Millwork Estimator</div>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-lg">
          <div className="data-label text-amber mb-4">v1.0 · Drawing-to-Estimate</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-medium">
            From shop drawings <span className="text-amber">to client-ready quotes</span> in under a minute.
          </h1>
          <p className="mt-6 text-ink-400 text-base leading-relaxed">
            Joiner reads your PDFs and elevations like a senior estimator. Cabinets, hardware, linear feet, finishes — every line item priced against your shop's price book.
          </p>

          <div className="mt-10 grid gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex gap-4 items-start group">
                <div className="h-10 w-10 shrink-0 rounded-md border border-ink-700 bg-ink-900 flex items-center justify-center text-amber group-hover:border-amber transition-colors duration-150">
                  {f.icon}
                </div>
                <div>
                  <div className="font-heading font-medium text-sm">{f.title}</div>
                  <div className="text-sm text-ink-400 leading-relaxed">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-ink-500 num">
          BUILT FOR CABINET SHOPS · USA · CANADA
        </div>
      </div>

      {/* Right panel - sign in */}
      <div className="flex flex-col justify-center items-center p-8 lg:p-14 relative">
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/4458210/pexels-photo-4458210.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative w-full max-w-sm">
          <div className="data-label mb-3 text-amber">Welcome</div>
          <h2 className="font-heading text-3xl tracking-tight font-medium mb-2">Sign in to your shop</h2>
          <p className="text-sm text-ink-400 mb-8">
            Use your Google account to access your projects, price book, and saved estimates.
          </p>
          <Button
            data-testid="google-login-button"
            onClick={handleGoogle}
            className="w-full h-12 bg-amber hover:bg-amber-hover text-ink-950 font-semibold rounded-md transition-colors duration-150 flex items-center justify-center gap-2"
          >
            <GoogleLogo size={20} weight="bold" />
            Continue with Google
          </Button>
          <div className="mt-8 flex items-start gap-2 text-xs text-ink-500">
            <Sparkle size={14} className="mt-0.5 text-amber" />
            <span>By continuing you agree this is a tool, not a contract. Always verify takeoffs against the drawings before sending a quote.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
